import React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';

import { accountSelector } from '../account/account-selectors';
import { loadAccount } from '../account/actions/load-account';
import { loadOrgs } from '../action/load-orgs';
import { addNotificationAction } from '../action/notifications/notifications';
import { createUser } from '../action/user/create-user';
import { orgsSelector } from '../reducers/org-selectors';
import { usersForAccountIdSelector } from '../reducers/user-selectors';
import { updateUser } from '../user/actions/update-user';

import { validatePhone, validateEmail } from '../businesslogic/form-validators';
import { UiButton } from '../ui/ui-button';
import { UiDialog } from '../ui/ui-dialog';
import { UiPhoneTextField } from '../ui/ui-phone-text-field';

import { UserPermissionForm } from './user-permission-form';

import { ACCOUNT_STATUS } from '../util/account-status';
import { formatPhoneNumber } from '../util/formatters';
import { HIRING_PERMISSION, EMPLOYEE_PERMISSION } from '../util/permissions';
import { sortAlphabetically, SORT_ORDER } from '../util/sort-functions';
import { isValidString } from '../util/string-validators';
import { LoadingMessage } from '../widgets/loading-message';

const validators = {
  firstName: {
    validator: (value: string | null) => (!isValidString(value) ? 'Required' : null),
  },
  lastName: {
    validator: (value: string | null) => (!isValidString(value) ? 'Required' : null),
  },
  email: {
    validator: validateEmail,
  },
  phoneNumber: {
    validator: validatePhone,
  },
  hiringPermission: {
    validator: (value: string | null) => (!isValidString(value) ? 'Required' : null),
  },
  employeesPermission: {
    validator: (value: string | null) => (!isValidString(value) ? 'Required' : null),
  },
  orgs: {
    validator: (value: OrgIds) =>
      value.length > 0 ? null : 'Select at least one active organization',
  },
  isAccountAdmin: { validator: () => null },
};

type FormKey = keyof typeof validators;
type FormKeyDictionary<T> = { [key in FormKey]: T };
type OrgIds = Array<UserOrganization>;
type UserFormUser = {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string | null;
  hiringPermission: string;
  employeesPermission: string;
  orgs?: OrgIds;
  isAccountAdmin: boolean;
};

interface InputProps {
  user?: UserFormUser | null;
  accountId: number;
  onClose: () => void;
  onAddUser?: (user: User) => void;
  disableAddAnother?: boolean;
}

const mapStateToProps = (state: any, { accountId }: InputProps) => ({
  users: usersForAccountIdSelector(state, accountId),
  account: accountSelector(state, accountId),
  allAccountOrgs: orgsSelector(state)?.filter((o) => o.accountId === accountId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { accountId }: InputProps,
) => ({
  actions: {
    loadAccount: () => dispatch(loadAccount(accountId)),
    loadOrgs: () => dispatch(loadOrgs(accountId)),
    createUser: (user: UserRequest) => dispatch(createUser(user)),
    updateUser: (username: string, update: UpdateUserRequest) =>
      dispatch(updateUser(username, update)),
    addNotification: (message: string) => dispatch(addNotificationAction({ message })),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

interface State {
  // Each key in the form will have a different value - some are strings, some are numbers, and some are arrays, so better to just leave this as an any
  values: FormKeyDictionary<any>;
  errors: FormKeyDictionary<string | null>;
  isCreatingUser: boolean;
  warningMessage: string | null;
}

const errorsObjectHasError = (errors: FormKeyDictionary<string | null>) =>
  Object.values(errors).filter(Boolean).length > 0;
const emptyErrorsObject = {
  firstName: null,
  lastName: null,
  email: null,
  phoneNumber: null,
  hiringPermission: null,
  employeesPermission: null,
  orgs: null,
  isAccountAdmin: null,
};

class UserFormComponent extends React.PureComponent<Props, State> {
  state: State = {
    values: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      hiringPermission: HIRING_PERMISSION.ALL,
      employeesPermission: EMPLOYEE_PERMISSION.ALL,
      orgs: [] as OrgIds,
      isAccountAdmin: false,
    },
    errors: emptyErrorsObject,
    isCreatingUser: false,
    warningMessage: null,
  };

  componentDidMount() {
    this.props.actions.loadOrgs();
    this.props.actions.loadAccount();
    this.setStateFromUser();
  }

  componentDidUpdate(prevProps: Props) {
    this.props.actions.loadOrgs();
    this.props.actions.loadAccount();

    const { user, allAccountOrgs } = this.props;
    if (prevProps.user !== user || prevProps.allAccountOrgs !== allAccountOrgs) {
      this.setStateFromUser();
    }
  }

  setStateFromUser = () => {
    const { user, allAccountOrgs, users } = this.props;

    if (!allAccountOrgs) {
      return;
    }

    let phoneNumber = '';
    if (user) {
      const formattedPhoneNumber = formatPhoneNumber(user.phoneNumber);
      if (formattedPhoneNumber) {
        phoneNumber = formattedPhoneNumber;
      }
    }
    const activeOrgIds = allAccountOrgs.filter((o) => o.isActive).map((o) => o.org_id);
    // IF (05/19/20): If we're editing a user, use the orgs they're already assigned to, if it's a
    // new user and there's only one org option, make that the default
    let userOrgs: OrgIds = [];
    if (user) {
      const activeUserOrgs = (user.orgs || []).filter((o) => activeOrgIds.includes(o.orgId));
      userOrgs = activeUserOrgs;
    } else if (activeOrgIds.length === 1) {
      userOrgs = [{ orgId: activeOrgIds[0] }];
    }

    this.setState({
      values: {
        firstName: user ? user.firstName : '',
        lastName: user ? user.lastName : '',
        email: user ? user.email : '',
        phoneNumber,
        hiringPermission: user ? user.hiringPermission : HIRING_PERMISSION.ALL,
        employeesPermission: user ? user.employeesPermission : HIRING_PERMISSION.ALL,
        orgs: userOrgs,
        // if we have 0 users when creating a new user, check isAccountAdmin by default
        isAccountAdmin: user ? user.isAccountAdmin : !users.length,
      },
      errors: emptyErrorsObject,
    });
  };

  clearWarningMessage = () => {
    this.setState({ warningMessage: null });
  };

  handlePermissionChange = (name: FormKey) => (value: string) => {
    this.handleChange(name, value);
  };

  handleNonPermissionChange = (name: FormKey) => (event: any) => {
    const { value } = event.currentTarget;
    if (name === 'phoneNumber') {
      this.handlePhoneNumberChange(value);
    } else {
      this.handleChange(name, value);
    }
  };

  handlePhoneNumberChange = (phoneNumber: string | null) => {
    this.setState((previousState) => {
      const values = { ...previousState.values, phoneNumber };
      const errors = { ...previousState.errors };
      return {
        values,
        errors,
      };
    });
  };

  handleChange = (name: FormKey, value: any) => {
    const { validator } = validators[name];
    let error: string | null = null;
    if (validator) {
      error = validator(value) || null;
    }
    this.setState((previousState) => {
      const values = { ...previousState.values, [name]: value };
      const errors = { ...previousState.errors, [name]: error };
      return {
        values,
        errors,
      };
    });
  };

  handleOrgClick = (orgId: number) => (e: any, checked: boolean) => {
    const orgs = checked
      ? this.state.values.orgs.concat([{ orgId }])
      : this.state.values.orgs.filter((org: UserOrganization) => org.orgId !== orgId);
    this.setState((previousState) => {
      const values = { ...previousState.values, orgs };
      const errors = { ...previousState.errors, orgs: validators.orgs.validator(orgs) };
      this.setState({ values, errors });
    });
  };

  handleAccountAdminClick = (_e: React.ChangeEvent<HTMLInputElement>, isAccountAdmin: boolean) => {
    this.setState((prevState) => ({ values: { ...prevState.values, isAccountAdmin } }));
  };

  addAnotherClicked = async () => {
    const success = await this.submit();
    if (success) {
      this.setStateFromUser();
    }
  };

  submitClicked = async () => {
    const success = await this.submit();
    if (success) {
      this.props.onClose();
    }
  };

  submit = async (): Promise<boolean> => {
    const { actions, accountId, user, account, onAddUser, allAccountOrgs } = this.props;
    const {
      firstName,
      lastName,
      email,
      phoneNumber,
      hiringPermission,
      employeesPermission,
      orgs,
      isAccountAdmin,
    } = this.state.values;

    this.setState({ isCreatingUser: true });
    const errors: FormKeyDictionary<string | null> = { ...emptyErrorsObject };
    (Object.keys(validators) as Array<FormKey>).forEach((f: FormKey) => {
      const error = validators[f].validator(this.state.values[f]);
      if (error) {
        errors[f] = error;
      }
    });

    if (errorsObjectHasError(errors)) {
      this.setState({
        errors,
        isCreatingUser: false,
      });
      return false;
    }

    // Inactive orgs can't be selected and won't be in the org value list
    // but the message to users is that we can't change inactive org user permissions so we shouldn't drop these user organizations
    const selectedInactiveUserOrgs = (user?.orgs || []).filter((uo) =>
      allAccountOrgs.find((org) => !org.isActive && uo.orgId === org.org_id),
    );

    const allUserOrgs = orgs.concat(selectedInactiveUserOrgs);

    const userRequest = {
      accountId,
      firstName,
      lastName,
      email: email.toLocaleLowerCase(),
      phoneNumber,
      hiringPermission,
      employeesPermission,
      orgs: allUserOrgs,
      isAccountAdmin,
    };

    if (user) {
      await actions.updateUser(user.username, userRequest);
      actions.addNotification('User updated successfully');
    } else {
      const result = await actions.createUser(userRequest);
      if ('warningMessage' in result) {
        this.setState({
          warningMessage: result.warningMessage,
          isCreatingUser: false,
        });
        return false;
      }
      if (onAddUser) {
        onAddUser(result);
      }
      const successMessage =
        account?.status === ACCOUNT_STATUS.PRE_TRIAL
          ? 'User created successfully, invite will be sent when trial starts'
          : `Great! We've sent an invite to ${firstName}`;
      actions.addNotification(successMessage);
    }

    this.setState({ isCreatingUser: false });
    return true;
  };

  render() {
    const { onClose, user, allAccountOrgs, disableAddAnother, users } = this.props;

    if (!allAccountOrgs) {
      return (
        <UiDialog size="xs" title={`${user ? 'Edit' : 'Create New'} User`}>
          <UiDialog.Content>
            <LoadingMessage />
          </UiDialog.Content>
        </UiDialog>
      );
    }
    const { values, errors, isCreatingUser, warningMessage } = this.state;
    const {
      firstName,
      lastName,
      email,
      phoneNumber,
      hiringPermission,
      employeesPermission,
      isAccountAdmin,
    } = values;
    const selectedOrgIds = values.orgs.map((org: UserOrganization) => org.orgId);
    const selectedInactiveOrgIds = (user?.orgs || []).reduce((acc, uo) => {
      if (allAccountOrgs.find((org) => !org.isActive && uo.orgId === org.org_id)) {
        acc.push(uo.orgId);
      }
      return acc;
    }, [] as number[]);
    // To not crowd the list of orgs for larger accounts, only display inactive orgs if the user belongs to them
    // This way we can surface the information to the user when relevant
    const orgsToDisplay = allAccountOrgs.filter(
      (org) => org.isActive || selectedInactiveOrgIds.includes(org.org_id),
    );

    const hasAnError = errorsObjectHasError(errors);
    const hasPermissionToNothing =
      employeesPermission === EMPLOYEE_PERMISSION.NONE &&
      hiringPermission === HIRING_PERMISSION.NONE;

    const isFirstUser = !users.length;
    const isOnlyAccountAdmin =
      user?.isAccountAdmin && users.filter((u) => u.isAccountAdmin).length === 1;
    const isAccountAdminDisabled = isFirstUser || isOnlyAccountAdmin;
    const accountAdminDisabledReason = isFirstUser
      ? 'The first user must be an account admin'
      : "Can't disable the only account admin";

    return (
      <>
        <UiDialog size="xs" onClose={onClose} title={`${user ? 'Edit' : 'Create New'} User`}>
          <UiDialog.Content>
            <TextField
              className="u-width-100"
              error={Boolean(errors.firstName)}
              helperText={errors.firstName}
              label="First Name"
              value={firstName}
              onChange={this.handleNonPermissionChange('firstName')}
              required
            />
            <TextField
              className="u-width-100 u-margin-top"
              error={Boolean(errors.lastName)}
              helperText={errors.lastName}
              label="Last Name"
              value={lastName}
              onChange={this.handleNonPermissionChange('lastName')}
              required
            />
            <TextField
              className="u-width-100 u-margin-top"
              error={Boolean(errors.email)}
              helperText={errors.email}
              label="Email"
              type="email"
              value={email}
              onChange={this.handleNonPermissionChange('email')}
              required
            />
            <UiPhoneTextField
              className="u-width-100 u-margin-top"
              label="Work Phone"
              value={phoneNumber}
              onChange={this.handlePhoneNumberChange}
            />
            <FormControl className="u-width-100">
              <div className="typo-label u-margin-top-xl">Administration</div>
              <div className="typo-subtitle u-margin-top-sm">
                Account admins have permission to manage billing, ad spend, and users. They are
                automatically subscribed to certain notifications, like when a new referral is
                created or receiving a new survey response.
              </div>
              <div className="u-margin-top-sm u-flex-center">
                <Checkbox
                  className="u-no-padding"
                  disabled={isAccountAdminDisabled}
                  checked={isAccountAdmin}
                  onChange={this.handleAccountAdminClick}
                />
                <Tooltip
                  title={accountAdminDisabledReason}
                  placement="right"
                  disableFocusListener={!isAccountAdminDisabled}
                  disableHoverListener={!isAccountAdminDisabled}
                  disableTouchListener={!isAccountAdminDisabled}
                >
                  <div className="typo-title u-margin-left-sm">Account admin</div>
                </Tooltip>
              </div>
            </FormControl>
            {orgsToDisplay.length > 1 && (
              <FormControl>
                <div className="typo-label u-margin-top-xl">Organizations</div>
                {orgsToDisplay
                  .sort(sortAlphabetically((o) => o.name, SORT_ORDER.ASC))
                  .map((org) => (
                    <div key={org.org_id} className="u-margin-top-sm u-flex-center">
                      <Checkbox
                        className="u-no-padding"
                        checked={
                          selectedOrgIds.includes(org.org_id) ||
                          selectedInactiveOrgIds.includes(org.org_id)
                        }
                        onChange={this.handleOrgClick(org.org_id)}
                        disabled={!org.isActive}
                      />
                      <Tooltip
                        title={org.isActive ? '' : 'Cannot change user access to inactive orgs'}
                        placement="right"
                        disableFocusListener={org.isActive}
                        disableHoverListener={org.isActive}
                        disableTouchListener={org.isActive}
                      >
                        <div
                          className={`typo-title u-margin-left-sm ${
                            org.isActive ? '' : 'u-color-gray'
                          }`}
                        >
                          {org.isActive ? '' : '(Inactive) '}
                          {org.name}
                        </div>
                      </Tooltip>
                    </div>
                  ))}
                <FormHelperText className="u-color-red">{errors.orgs}</FormHelperText>
              </FormControl>
            )}
            <UserPermissionForm
              hiringPermission={hiringPermission}
              employeesPermission={employeesPermission}
              onHiringPermissionChange={this.handlePermissionChange('hiringPermission')}
              onEmployeePermissionChange={this.handlePermissionChange('employeesPermission')}
            />
          </UiDialog.Content>
          <UiDialog.Actions>
            <UiButton disabled={isCreatingUser} onClick={onClose} variant="dismiss">
              Cancel
            </UiButton>
            <div>
              {!user && !disableAddAnother && (
                <UiButton
                  loading={isCreatingUser}
                  variant="secondary"
                  className="u-margin-right"
                  disabled={hasAnError || hasPermissionToNothing}
                  onClick={this.addAnotherClicked}
                >
                  Add Another
                </UiButton>
              )}
              <UiButton
                loading={isCreatingUser}
                disabled={hasAnError || hasPermissionToNothing}
                onClick={this.submitClicked}
              >
                {user ? 'Update' : 'Done'}
              </UiButton>
            </div>
          </UiDialog.Actions>
        </UiDialog>
        {warningMessage && (
          <UiDialog onClose={this.clearWarningMessage}>
            <UiDialog.Content>{warningMessage}</UiDialog.Content>
            <UiDialog.Actions>
              <UiButton onClick={this.clearWarningMessage}>Okay</UiButton>
            </UiDialog.Actions>
          </UiDialog>
        )}
      </>
    );
  }
}

export const UserForm = connect(mapStateToProps, mapDispatchToProps)(UserFormComponent);
